import React, { useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { includes, sortBy, uniq, uniqBy, xor } from "lodash";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterTextArea,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { useStore as useAuthStore } from "auth/store";
import { useRolesBasedGetCleaningCompanies } from "core/queries/GetCleaningCompanies";
import {
  GetUsersResponse,
  GetUsersVariables,
  getUsers,
} from "core/queries/GetUsers";
import { mapUserDataToProps } from "core/queries/mappers";
import { setFilters } from "tasks/actions";
import { AssigneeFilterGroupItem } from "tasks/components/Filters/AssigneeFilterGroupItem";
import { GET_USER, TNS_USER } from "tasks/constants";
import { parseGraphQLId } from "tasks/helpers";
import { useSegmentQueue } from "tasks/hooks";
import { useStore } from "tasks/store";

interface AssigneeFilterProps {
  shouldLoadCleaningCompanyEmployees?: boolean;
}

export const AssigneeFilters = (props: AssigneeFilterProps) => {
  const { shouldLoadCleaningCompanyEmployees = false } = props;
  const {
    state: { filters: globalFilters },
    dispatch,
  } = useStore();

  const { assignee: assigneeIds } = globalFilters;
  const {
    state: { user: loggedInUser },
  } = useAuthStore();
  const [search, setSearch] = useState("");
  const pushToSegmentQueue = useSegmentQueue(globalFilters);
  const cityId = loggedInUser?.cityId;
  const userCleaningCompanyIds =
    shouldLoadCleaningCompanyEmployees &&
    (loggedInUser?.cleaningCompanies || []).map(c => c.id);

  const onClickOption = (user: User, isChecked: boolean) => {
    const userId = parseGraphQLId(user.id);
    if (!userId) return;

    const newAssignees = xor(assigneeIds, [userId]);
    dispatch(setFilters({ assignee: newAssignees }));
    pushToSegmentQueue({
      eventName: "assignee_filters",
      action: "click",
      element: `assignee_filters-${userId}`,
      props: { nextValue: !isChecked ? "selected" : "deselected" },
      commonProps: {},
    });
  };

  // This will only load the Companies in a City if the user has the appropriate roles.
  const {
    data: companyData,
    error: companyError,
    loading: companyLoading,
  } = useRolesBasedGetCleaningCompanies({
    skip: !shouldLoadCleaningCompanyEmployees || !cityId,
    variables: {
      filters: {
        active: true,
        cityId: cityId || "",
      },
    },
  });
  const { data, error, loading } = useQuery<
    GetUsersResponse,
    GetUsersVariables
  >(getUsers, {
    skip: !cityId,
    variables: {
      filterCity: cityId || "",
    },
  });
  // For HKMs, we also need to show you Employees in your Cleaning Company.
  // For anyone who manages multiple companies, we load all Companies in a City.
  // But currently only in 1 case. Scheduler.
  const filterCompanies = shouldLoadCleaningCompanyEmployees
    ? uniq([
        ...(userCleaningCompanyIds || []),
        ...(companyData?.cleaningCompany || []).map(c => c.id),
      ]).filter(x => !!x)
    : [];
  const {
    data: cleaningCompanyEmployeesData,
    error: cleaningCompanyEmployeesError,
    loading: cleaningCompanyEmployeesLoading,
  } = useQuery<GetUsersResponse, GetUsersVariables>(getUsers, {
    skip:
      !shouldLoadCleaningCompanyEmployees ||
      companyLoading ||
      filterCompanies.length === 0,
    variables: {
      filterCompanies,
    },
  });
  const allUsers = uniqBy(
    [
      ...(data?.userConnections?.users || []),
      ...(cleaningCompanyEmployeesData?.userConnections?.users || []),
      GET_USER,
      TNS_USER,
    ],
    u => u.id,
  );
  const users = sortBy(allUsers.map(mapUserDataToProps), ["firstName"])
    .filter((user: User) => {
      if (search === "") return true;

      const userId = parseGraphQLId(user.id);
      const isChecked = includes(assigneeIds, userId);
      return (
        isChecked || user.name.toLowerCase().includes(search.toLowerCase())
      );
    })
    .map(user => {
      const userId = parseGraphQLId(user.id);
      const isChecked = includes(assigneeIds, userId);
      return (
        <AssigneeFilterGroupItem
          isChecked={isChecked}
          key={userId}
          user={user}
          onClick={() => onClickOption(user, isChecked)}
        />
      );
    });
  const isEmpty = users.length === 0;
  const isEmptyAndSearched = isEmpty && search !== "";
  const totalSelected = (assigneeIds && assigneeIds.length) || 0;

  if (
    loading ||
    companyLoading ||
    cleaningCompanyEmployeesLoading ||
    !!error ||
    !!companyError ||
    !!cleaningCompanyEmployeesError
  )
    return null;

  return (
    <Dropdown
      trigger={
        <Button fullWidth iconRight type="button">
          Assignees
          {totalSelected > 0 && ` (${totalSelected})`}
          <ChevronDown />
        </Button>
      }
    >
      {({ isOpen: isPortalOpen }) =>
        isPortalOpen && (
          <FilterDropdownContent align="center">
            <FilterTextArea
              autoFocus
              disabled={loading || !!error}
              placeholder={`Search ${users.length} assignees`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {isEmptyAndSearched && (
              <FilterEmpty>{`No Results for ${search}.`}</FilterEmpty>
            )}
            <FilterButtonList>{users}</FilterButtonList>
            <FilterFooter>
              {`${totalSelected || 0} Selected`}
              {totalSelected > 0 && (
                <Link
                  onClick={() => {
                    requestAnimationFrame(() =>
                      dispatch(setFilters({ assignee: [] })),
                    );
                  }}
                >
                  Clear selected
                </Link>
              )}
            </FilterFooter>
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
